import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { getSubjectGrades, getSubjectAttendance, createGradeColumn, deleteGradeColumn, setGrade, setAttendance, uploadSubjectDocument, getSubjectDocuments, deleteSubjectDocument, getDocumentUrl } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ArrowLeft, Plus, Trash2, BookOpen, ClipboardList, FileText, Upload, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const ATT_OPTIONS = [
  { value: 'present', label: 'P', full: 'Presente', color: 'bg-[#2D6A4F] text-white' },
  { value: 'absent', label: 'A', full: 'Ausente', color: 'bg-[#9B2226] text-white' },
  { value: 'justified', label: 'J', full: 'Justificado', color: 'bg-[#B8860B] text-white' },
];

export default function TeacherGrades() {
  const { subjectId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('grades');
  const [gradesData, setGradesData] = useState(null);
  const [attendanceData, setAttendanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddColumn, setShowAddColumn] = useState(false);
  const [newColumnTitle, setNewColumnTitle] = useState('');
  const [subjectTitle, setSubjectTitle] = useState('');
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);

  const fetchGrades = useCallback(async () => {
    try {
      const data = await getSubjectGrades(subjectId);
      setGradesData(data);
    } catch (e) { console.error(e); }
  }, [subjectId]);

  const fetchAttendance = useCallback(async () => {
    try {
      const data = await getSubjectAttendance(subjectId);
      setAttendanceData(data);
    } catch (e) { console.error(e); }
  }, [subjectId]);

  const fetchDocuments = useCallback(async () => {
    try {
      const data = await getSubjectDocuments(subjectId);
      setDocuments(data);
    } catch (e) { console.error(e); }
  }, [subjectId]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchGrades(), fetchAttendance(), fetchDocuments()]);
      setLoading(false);
    };
    init();
  }, [fetchGrades, fetchAttendance, fetchDocuments]);

  // Get subject title from URL params or grades data
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setSubjectTitle(params.get('title') || 'Curso');
  }, []);

  const handleAddColumn = async () => {
    if (!newColumnTitle.trim()) return;
    try {
      await createGradeColumn(subjectId, newColumnTitle.trim());
      setNewColumnTitle('');
      setShowAddColumn(false);
      await fetchGrades();
      toast.success('Columna creada');
    } catch (e) { toast.error('Error al crear columna'); }
  };

  const handleDeleteColumn = async (colId) => {
    if (!window.confirm('Eliminar esta columna y todas sus notas?')) return;
    try {
      await deleteGradeColumn(colId);
      await fetchGrades();
      toast.success('Columna eliminada');
    } catch (e) { toast.error('Error al eliminar'); }
  };

  const handleGradeChange = async (studentId, columnId, value) => {
    const num = parseFloat(value);
    if (isNaN(num) || num < 1.0 || num > 7.0) return;
    try {
      await setGrade(subjectId, studentId, columnId, num);
      await fetchGrades();
    } catch (e) { toast.error('Error al guardar nota'); }
  };

  const handleAttendanceChange = async (classId, studentId, status) => {
    try {
      await setAttendance(classId, studentId, status);
      await fetchAttendance();
    } catch (e) { toast.error('Error al registrar asistencia'); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadSubjectDocument(subjectId, file);
      await fetchDocuments();
      toast.success('Documento subido');
    } catch (err) { toast.error('Error al subir documento'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Eliminar este documento?')) return;
    try {
      await deleteSubjectDocument(subjectId, docId);
      await fetchDocuments();
      toast.success('Documento eliminado');
    } catch (e) { toast.error('Error al eliminar'); }
  };

  const getGradeColor = (val) => {
    if (val === null || val === undefined) return 'text-[#737373]';
    if (val >= 5.0) return 'text-[#2D6A4F] font-semibold';
    if (val >= 4.0) return 'text-[#B8860B] font-semibold';
    return 'text-[#9B2226] font-semibold';
  };

  if (loading) return <Layout><div className="p-6 flex items-center justify-center min-h-[60vh]"><div className="spinner" /></div></Layout>;

  return (
    <Layout>
      <div className="p-4 md:p-6" data-testid="teacher-grades-page">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" onClick={() => navigate(-1)} className="text-[#4A4A4A] hover:text-[#1A1A1A] hover:bg-[#F5F2EA]">
            <ArrowLeft className="h-4 w-4 mr-1" /> Volver
          </Button>
          <div>
            <h1 className="font-heading text-xl md:text-2xl font-medium text-[#1A1A1A]">Notas y Asistencia</h1>
            <p className="text-sm text-[#737373]">{subjectTitle}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            onClick={() => setTab('grades')}
            variant={tab === 'grades' ? 'default' : 'outline'}
            className={tab === 'grades' ? 'bg-[#8B1538] hover:bg-[#70102B] text-white' : 'border-[#E5DFD3]'}
            data-testid="tab-grades"
          >
            <BookOpen className="h-4 w-4 mr-2" /> Notas
          </Button>
          <Button
            onClick={() => setTab('attendance')}
            variant={tab === 'attendance' ? 'default' : 'outline'}
            className={tab === 'attendance' ? 'bg-[#8B1538] hover:bg-[#70102B] text-white' : 'border-[#E5DFD3]'}
            data-testid="tab-attendance"
          >
            <ClipboardList className="h-4 w-4 mr-2" /> Asistencia
          </Button>
          <Button
            onClick={() => setTab('documents')}
            variant={tab === 'documents' ? 'default' : 'outline'}
            className={tab === 'documents' ? 'bg-[#8B1538] hover:bg-[#70102B] text-white' : 'border-[#E5DFD3]'}
            data-testid="tab-documents"
          >
            <FileText className="h-4 w-4 mr-2" /> Documentos ({documents.length})
          </Button>
        </div>

        {/* GRADES TAB */}
        {tab === 'grades' && (
          <Card className="bg-white border border-[#E5DFD3] shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg text-[#1A1A1A]">Libro de Notas (1.0 - 7.0)</CardTitle>
                <Button size="sm" onClick={() => setShowAddColumn(true)} className="bg-[#8B1538] hover:bg-[#70102B] text-white" data-testid="add-column-btn">
                  <Plus className="h-4 w-4 mr-1" /> Nueva Nota
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {!gradesData?.students?.length ? (
                <div className="text-center py-8 text-[#737373]">No hay estudiantes inscritos en este modulo</div>
              ) : !gradesData?.columns?.length ? (
                <div className="text-center py-8">
                  <BookOpen className="h-10 w-10 mx-auto text-[#D9B48F] mb-3" />
                  <p className="text-[#737373] mb-3">No hay columnas de notas. Crea una para comenzar.</p>
                  <Button size="sm" onClick={() => setShowAddColumn(true)} className="bg-[#8B1538] hover:bg-[#70102B] text-white">
                    <Plus className="h-4 w-4 mr-1" /> Crear Primera Nota
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#E5DFD3]">
                        <th className="text-left py-2 px-2 font-medium text-[#737373] min-w-[180px] sticky left-0 bg-white">Estudiante</th>
                        {gradesData.columns.map(col => (
                          <th key={col.id} className="text-center py-2 px-2 font-medium text-[#737373] min-w-[90px]">
                            <div className="flex items-center justify-center gap-1">
                              <span className="truncate max-w-[70px]" title={col.title}>{col.title}</span>
                              <button onClick={() => handleDeleteColumn(col.id)} className="p-0.5 rounded hover:bg-[#9B2226]/10" title="Eliminar">
                                <Trash2 className="h-3 w-3 text-[#9B2226]" />
                              </button>
                            </div>
                          </th>
                        ))}
                        <th className="text-center py-2 px-2 font-semibold text-[#8B1538] min-w-[80px]">Promedio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gradesData.students.map(st => (
                        <tr key={st.student_id} className="border-b border-[#E5DFD3]/50 hover:bg-[#F5F2EA]/50">
                          <td className="py-2 px-2 sticky left-0 bg-white">
                            <p className="font-medium text-[#1A1A1A]">{st.name}</p>
                            <p className="text-[10px] text-[#737373]">{st.email}</p>
                          </td>
                          {gradesData.columns.map(col => (
                            <td key={col.id} className="py-2 px-1 text-center">
                              <input
                                type="number"
                                min="1" max="7" step="0.1"
                                defaultValue={st.grades[col.id] ?? ''}
                                placeholder="-"
                                onBlur={(e) => {
                                  if (e.target.value) handleGradeChange(st.student_id, col.id, e.target.value);
                                }}
                                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                className={`w-16 text-center border border-[#E5DFD3] rounded px-1 py-1 text-sm focus:border-[#8B1538] focus:outline-none ${getGradeColor(st.grades[col.id])}`}
                                data-testid={`grade-input-${st.student_id}-${col.id}`}
                              />
                            </td>
                          ))}
                          <td className={`py-2 px-2 text-center text-base ${getGradeColor(st.average)}`}>
                            {st.average !== null ? st.average : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ATTENDANCE TAB */}
        {tab === 'attendance' && (
          <Card className="bg-white border border-[#E5DFD3] shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-lg text-[#1A1A1A]">Registro de Asistencia</CardTitle>
            </CardHeader>
            <CardContent>
              {!attendanceData?.students?.length ? (
                <div className="text-center py-8 text-[#737373]">No hay estudiantes inscritos</div>
              ) : !attendanceData?.classes?.length ? (
                <div className="text-center py-8">
                  <ClipboardList className="h-10 w-10 mx-auto text-[#D9B48F] mb-3" />
                  <p className="text-[#737373]">No hay clases programadas para este curso</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <div className="flex items-center gap-4 mb-3 text-xs text-[#737373]">
                    <span className="flex items-center gap-1"><span className="h-4 w-4 rounded bg-[#2D6A4F] text-white text-[10px] flex items-center justify-center font-bold">P</span> Presente</span>
                    <span className="flex items-center gap-1"><span className="h-4 w-4 rounded bg-[#9B2226] text-white text-[10px] flex items-center justify-center font-bold">A</span> Ausente</span>
                    <span className="flex items-center gap-1"><span className="h-4 w-4 rounded bg-[#B8860B] text-white text-[10px] flex items-center justify-center font-bold">J</span> Justificado</span>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#E5DFD3]">
                        <th className="text-left py-2 px-2 font-medium text-[#737373] min-w-[180px] sticky left-0 bg-white">Estudiante</th>
                        {attendanceData.classes.map(cl => (
                          <th key={cl.id} className="text-center py-2 px-1 font-medium text-[#737373] min-w-[60px]">
                            <div className="text-[10px] leading-tight">
                              <p>{new Date(cl.scheduled_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}</p>
                            </div>
                          </th>
                        ))}
                        <th className="text-center py-2 px-2 font-semibold text-[#8B1538] min-w-[60px]">%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendanceData.students.map(st => (
                        <tr key={st.student_id} className="border-b border-[#E5DFD3]/50 hover:bg-[#F5F2EA]/50">
                          <td className="py-2 px-2 sticky left-0 bg-white">
                            <p className="font-medium text-[#1A1A1A]">{st.name}</p>
                          </td>
                          {attendanceData.classes.map(cl => {
                            const current = st.attendance[cl.id];
                            return (
                              <td key={cl.id} className="py-1 px-1 text-center">
                                <div className="flex justify-center">
                                  <select
                                    value={current || ''}
                                    onChange={(e) => handleAttendanceChange(cl.id, st.student_id, e.target.value)}
                                    className={`w-10 h-7 text-[10px] font-bold rounded border-0 text-center cursor-pointer ${
                                      current === 'present' ? 'bg-[#2D6A4F] text-white' :
                                      current === 'absent' ? 'bg-[#9B2226] text-white' :
                                      current === 'justified' ? 'bg-[#B8860B] text-white' :
                                      'bg-[#E5DFD3] text-[#737373]'
                                    }`}
                                    data-testid={`att-${st.student_id}-${cl.id}`}
                                  >
                                    <option value="">-</option>
                                    {ATT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                  </select>
                                </div>
                              </td>
                            );
                          })}
                          <td className={`py-2 px-2 text-center font-semibold ${st.attendance_percent >= 75 ? 'text-[#2D6A4F]' : 'text-[#9B2226]'}`}>
                            {st.attendance_percent}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* DOCUMENTS TAB */}
        {tab === 'documents' && (
          <Card className="bg-white border border-[#E5DFD3] shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg text-[#1A1A1A]">Documentos del Curso</CardTitle>
                <label className="cursor-pointer">
                  <input type="file" className="hidden" onChange={handleFileUpload} disabled={uploading} data-testid="upload-doc-input" />
                  <span className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#8B1538] hover:bg-[#70102B] text-white rounded-md text-sm font-medium transition-colors">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    {uploading ? 'Subiendo...' : 'Subir Documento'}
                  </span>
                </label>
              </div>
            </CardHeader>
            <CardContent>
              {documents.length > 0 ? (
                <div className="space-y-3">
                  {documents.map(doc => (
                    <div key={doc.id} data-testid={`doc-${doc.id}`} className="flex items-center justify-between p-3 bg-[#F5F2EA] rounded-md">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-md bg-[#8B1538]/10 flex items-center justify-center">
                          <FileText className="h-5 w-5 text-[#8B1538]" />
                        </div>
                        <div>
                          <p className="font-medium text-[#1A1A1A] text-sm">{doc.filename}</p>
                          <p className="text-xs text-[#737373]">
                            {(doc.size / 1024).toFixed(1)} KB — {doc.uploaded_by_name} — {new Date(doc.uploaded_at).toLocaleDateString('es-ES')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <a href={getDocumentUrl(doc.storage_path)} target="_blank" rel="noopener noreferrer">
                          <Button variant="ghost" size="sm" className="text-[#8B1538]"><Download className="h-4 w-4" /></Button>
                        </a>
                        <Button variant="ghost" size="sm" className="text-[#9B2226] hover:bg-[#9B2226]/10" onClick={() => handleDeleteDoc(doc.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="h-10 w-10 mx-auto text-[#D9B48F] mb-3" />
                  <p className="text-[#737373]">No hay documentos. Sube el primero para tus estudiantes.</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Add Column Dialog */}
        <Dialog open={showAddColumn} onOpenChange={setShowAddColumn}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-heading">Nueva Columna de Nota</DialogTitle>
            </DialogHeader>
            <Input
              value={newColumnTitle}
              onChange={(e) => setNewColumnTitle(e.target.value)}
              placeholder="Ej: Examen 1, Trabajo Práctico..."
              className="border-[#E5DFD3]"
              data-testid="new-column-input"
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddColumn(); }}
              autoFocus
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddColumn(false)} className="border-[#E5DFD3]">Cancelar</Button>
              <Button onClick={handleAddColumn} className="bg-[#8B1538] hover:bg-[#70102B] text-white" data-testid="confirm-add-column">Crear</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
